import * as React from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Select } from '@factorialco/f0-react'
import { Input, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import {
  AtsOfferLetterTemplateDataToFillFormDocument,
  AtsSendOfferLetterGenerateFileFromVariablesDocument,
  AtsSendOfferLetterGeneratedFileFragment,
  AtsSendOfferLetterGetTemplatePreviewDocument,
  AtsSendOfferLetterGetTemplatesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PdfViewer } from 'modules/ats/components/PdfViewer'

import { OfferLetterFromTemplateFilePreview } from './PreviewContent'
import { getSystemVariableDefaults } from './getSystemVariableDefaults'

interface FormValues {
  templateId: number
  filename: string
}

interface Props {
  jobApplicationId: number
  onClose: () => void
}

export const OfferLetterFromTemplateModal = ({ jobApplicationId, onClose }: Props) => {
  const { company, employee, email } = useCurrentSession()
  const [content, setContent] = React.useState<'form' | 'preview'>('form')
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(null)
  const [finalPreviewFile, setFinalPreviewFile] =
    React.useState<AtsSendOfferLetterGeneratedFileFragment | null>(null)
  const [variableValues, setVariableValues] = React.useState<Record<string, string>>({})

  const handleVariableChange = (name: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value)
  }

  const { data: templatesData } = useQuery(AtsSendOfferLetterGetTemplatesDocument)
  const { data: previewData, loading: isPreviewLoading } = useQuery(
    AtsSendOfferLetterGetTemplatePreviewDocument,
    {
      variables: { id: Number(selectedTemplateId) },
      skip: !selectedTemplateId,
    }
  )
  const { data: applicationData } = useQuery(AtsOfferLetterTemplateDataToFillFormDocument, {
    variables: { jobApplicationId },
  })

  const [generateFileFromVariables] = useMutation(
    AtsSendOfferLetterGenerateFileFromVariablesDocument,
    {
      extractorFn: (data) => data.atsMutations.createFromVariablesAtsFile,
    }
  )

  const templateOptions =
    templatesData?.ats.offerLetterTemplates.map((template) => ({
      label: template.name,
      value: template.id.toString(),
    })) ?? []

  const previewUrl = previewData?.ats.offerLetterTemplate?.atsFile?.preview?.url ?? ''
  const pdfFilename = previewData?.ats.offerLetterTemplate?.atsFile?.preview?.filename ?? ''

  const customVariables = previewData?.ats.offerLetterTemplate?.customVariables ?? []
  const systemVariables = React.useMemo(
    () => previewData?.ats.offerLetterTemplate?.systemVariables ?? [],
    [previewData?.ats.offerLetterTemplate?.systemVariables]
  )

  React.useEffect(() => {
    if (!selectedTemplateId) return

    const variableNames = systemVariables.map((v) => v.name)
    const defaults = getSystemVariableDefaults(variableNames, applicationData?.ats.application, {
      company,
      employee,
      email: email ?? '',
    })
    setVariableValues(defaults)
  }, [selectedTemplateId, systemVariables, applicationData, company, employee, email])

  const form = useForm<FormValues>({
    defaultValues: {
      templateId: 0,
      filename: '',
    },
    validate(values) {
      const errors: FormErrors<FormValues> = {}
      if (!selectedTemplateId) {
        errors.templateId = i18n.t('common.validations.empty')
      }
      if (!values.filename) {
        errors.filename = i18n.t('common.validations.empty')
      }

      return errors
    },
    validateOnChange: false,
    validateOnBlur: false,
    async onSubmit(_values, { setErrors }) {
      const result = await generateFileFromVariables({
        variables: {
          id: previewData?.ats.offerLetterTemplate?.atsFile?.id ?? 0,
          jobApplicationId,
          filename: form.values.filename,
          resourceType: 'application',
          variables: Object.entries(variableValues).map(([name, value]) => ({ name, value })),
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success && !!handled.data?.file) {
        setContent('preview')
        setFinalPreviewFile(handled.data.file)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.send_offer_letter_modal.from_template.error_generating_file'),
        })
      }

      return handled
    },
  })

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.send_offer_letter_modal.from_template.title')}
      position="fullscreen"
      primaryAction={
        content === 'form'
          ? {
              label: i18n.t('common.next'),
              onClick: form.handleSubmit,
              loading: form.isSubmitting,
            }
          : {
              label: 'Send',
              onClick: () => onClose(), // TO DO: replace by onSend prop when ready
            }
      }
      secondaryAction={
        content === 'form'
          ? {
              label: i18n.t('common.cancel'),
              onClick: () => onClose(),
            }
          : {
              label: i18n.t('common.go_back'),
              onClick: () => {
                setContent('form')
                setFinalPreviewFile(null)
              },
            }
      }
      disableContentPadding
    >
      <Box height="full">
        {content === 'form' ? (
          <Box flexGrow flexDirection="row" overflowY="hidden">
            <Box flexGrow height="full" overflowY="auto">
              {isPreviewLoading ? (
                <Box
                  height="full"
                  justifyContent="center"
                  alignItems="center"
                  gap="s24"
                  background="grey100"
                >
                  <Spinner />
                </Box>
              ) : previewUrl ? (
                <PdfViewer url={previewUrl} filename={pdfFilename} initialScale="page-width" />
              ) : (
                <Box
                  height="full"
                  justifyContent="center"
                  alignItems="center"
                  gap="s24"
                  background="grey100"
                >
                  <OneEmptyState title="No document preview" emoji="📄" />
                </Box>
              )}
            </Box>
            <Box
              flexNoShrink
              height="full"
              overflowX="hidden"
              border={{ left: { style: 'solid', width: 's1', color: 'grey200' } }}
              style={{ width: '36%' }}
              paddingY="s24"
              paddingX="s16"
              gap="s24"
            >
              <Box gap="s16">
                <Text.Normal size="300" color="textPrimary" weight="semibold">
                  {i18n.t('ats.send_offer_letter_modal.from_template.edit_offer_letter')}
                </Text.Normal>
                <F0Select
                  label="Select template"
                  name="templateId"
                  placeholder={i18n.t('common.select')}
                  onChange={handleTemplateChange}
                  size="md"
                  value={selectedTemplateId ?? undefined}
                  options={templateOptions}
                  searchBoxPlaceholder={i18n.t(
                    'ats.jobs.new_job_modal.workplace_search_placeholder'
                  )}
                  searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                  error={form.errors.templateId}
                />
                <Input
                  label={i18n.t('ats.send_offer_letter_modal.from_template.file_name_label')}
                  name="filename"
                  placeholder={i18n.t(
                    'ats.send_offer_letter_modal.from_template.file_name_placeholder'
                  )}
                  size="md"
                  onChange={(value) => form.setFieldValue('filename', value)}
                  value={form.values.filename}
                  error={form.errors.filename}
                />
              </Box>
              {(customVariables.length > 0 || systemVariables.length > 0) && (
                <Box gap="s16">
                  <Text.Normal size="300" color="textPrimary" weight="semibold">
                    {i18n.t('ats.send_offer_letter_modal.from_template.editable_fields')}
                  </Text.Normal>
                  {systemVariables.map((variable) => (
                    <Input
                      key={variable.name}
                      label={variable.label}
                      name={variable.name}
                      size="md"
                      onChange={(value) => handleVariableChange(variable.name, value)}
                      value={variableValues[variable.name] ?? ''}
                    />
                  ))}
                  {customVariables.map((variable) => (
                    <Input
                      key={variable.name}
                      label={variable.label}
                      name={variable.name}
                      size="md"
                      onChange={(value) => handleVariableChange(variable.name, value)}
                      value={variableValues[variable.name] ?? ''}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <OfferLetterFromTemplateFilePreview
            url={finalPreviewFile?.file.url ?? ''}
            filename={finalPreviewFile?.file.filename ?? ''}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
